import "moment-timezone";
import React, { useState, useEffect, useRef } from "react";
import { Button, Notification } from "@telia/teddy";
import moment from "moment";
import { useDispatch } from "react-redux";
import { emptyOrder, extendOrder } from "../../../../checkout/state/actions/cartActions";
import "./BookingWarning.less";

const BOOKING_WARNING_THRESHOLD_IN_SECONDS = 5 * 60 * 1000; //5 mins left of 30 mins
const BOOKING_TIME = 30 * 60 * 1000; // 30 minutes

interface BookingWarningProps {
    bookEndTime?: string;
    bookStartTime?: string;
}

const BookingWarning: React.FC<BookingWarningProps> = ({ bookStartTime, bookEndTime }) => {
    const dispatch = useDispatch();
    const [milliesPassed, setMilliesPassed] = useState(0);
    const [initialMilliesLeft, setInitialMilliesLeft] = useState(BOOKING_TIME);
    const previousBookEndTimeRef = useRef(bookEndTime);
    const startTimestamp = moment(bookStartTime);

    // Reset elapsed time when order is extended (bookEndTime changes)
    useEffect(() => {
        if (previousBookEndTimeRef.current !== bookEndTime) {
            setMilliesPassed(0);
        }
        previousBookEndTimeRef.current = bookEndTime;
    }, [bookEndTime]);

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | undefined;

        // Empty order if booking expires
        if (initialMilliesLeft - milliesPassed < 0) {
            clearInterval(interval);
        } else {
            interval = setInterval(() => {
                const nowTimestamp = moment().tz("Europe/Oslo");
                const delta = moment.duration(nowTimestamp.diff(startTimestamp)).asMilliseconds();
                setMilliesPassed(delta);
            }, 250);
        }

        return () => clearInterval(interval);
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, [milliesPassed, initialMilliesLeft, startTimestamp, emptyOrder]);

    useEffect(() => {
        const bookEndTimestamp = moment(bookEndTime);
        const duration = moment.duration(bookEndTimestamp.diff(moment(bookStartTime)));
        setInitialMilliesLeft(duration.asMilliseconds());
    }, [bookStartTime, bookEndTime]);

    const shouldShowBookEndTime =
        initialMilliesLeft - milliesPassed > 0 &&
        initialMilliesLeft - milliesPassed < BOOKING_WARNING_THRESHOLD_IN_SECONDS;
    if (!shouldShowBookEndTime || !initialMilliesLeft) {
        return null;
    }

    const formattedTimeLeft = moment.utc(initialMilliesLeft - milliesPassed).format("mm:ss");

    return (
        <div className="booking-warning">
            <div className="booking-warning__notification">
                <Notification variant="information">
                    <Notification.Icon name="time" className="booking-warning__icon" />
                    <Notification.Text className="booking-warning__notification-text">
                        Vi har reservert varene i handlekurven for deg. For å sikre deg varene, fortsett kjøpet innen{" "}
                        <span className="booking-warning__timeleft">{formattedTimeLeft}.</span>
                        <Button
                            className="booking-warning__button"
                            type="button"
                            variant="text"
                            onClick={() => dispatch(extendOrder())}
                        >
                            Trykk her hvis du trenger mer tid
                        </Button>
                    </Notification.Text>
                </Notification>
            </div>
        </div>
    );
};

export default BookingWarning;
